import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, downloadFile } from '../../api';
import { useToast } from '../../ToastContext';
import BidsTab from './BidsTab';
import { parseDate, fmtDate as fmtCalendarDate } from '../../dateUtil';
import { STEP_LABELS, stepsFor, actorFor, nextStep, cancellable } from '../../stepFlows';
import { formatCurrency } from '../../currencyUtil';

function Countdown({ date }) {
  if (!date) return <span className="text-slate-400">—</span>;
  // Due dates are calendar days — payment isn't overdue until that day has ended locally.
  const due = parseDate(date);
  const deadline = new Date(due.getFullYear(), due.getMonth(), due.getDate() + 1);
  const diff = deadline - new Date();
  if (diff < 0) return <span className="text-red-600 font-semibold">Overdue</span>;
  const days = Math.floor(diff / 86400000);
  if (days > 1) return <span>{days} days</span>;
  if (days === 1) return <span className="text-amber-600 font-semibold">1 day</span>;
  return <span className="text-red-600 font-semibold">Today</span>;
}

function StepBadge({ step }) {
  const colors = {
    awaiting_payment:     'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800',
    payment_confirmed:    'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:ring-teal-800',
    awaiting_inspection:  'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:ring-sky-800',
    inspection_scheduled: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800',
    awaiting_pickup:      'bg-purple-50 text-purple-700 ring-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-800',
    closed:               'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800',
    cancelled:            'bg-red-50 text-red-600 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800',
  };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ring-1 ${colors[step] || ''}`}>
      {STEP_LABELS[step]}
    </span>
  );
}

function StepTracker({ step, flow }) {
  const steps = stepsFor(flow);
  const current = steps.indexOf(step);
  return (
    <div className="flex items-center gap-1 mt-3">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-1 ${i <= current ? 'text-blue-600 dark:text-blue-400' : 'text-slate-300 dark:text-slate-600'}`}>
            <div className={`w-2 h-2 rounded-full ${i < current ? 'bg-blue-600 dark:bg-blue-400' : i === current ? 'bg-blue-600 dark:bg-blue-400 ring-2 ring-blue-200 dark:ring-blue-800' : 'bg-slate-200 dark:bg-slate-700'}`} />
            <span className="text-xs font-medium hidden sm:block">{STEP_LABELS[s]}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-px ${i < current ? 'bg-blue-400 dark:bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function carLabel(t) {
  return [t.year, t.make, t.model, t.trim].filter(Boolean).join(' ') || t.vin || `Car #${t.car_id}`;
}

function money(n) {
  if (n == null) return '—';
  return `$${formatCurrency(n)}`;
}

function fmtDate(date) {
  if (!date) return '—';
  return fmtCalendarDate(date);
}

function WaitingMsg({ children }) {
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500">
      <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {children}
    </div>
  );
}

function CancelDialog({ dialog, onClose, onConfirm, loading, triggerRef }) {
  // Escape-to-close and focus restore, matching the Buy Now confirmation modal in DealView.jsx.
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, onClose]);

  const handleClose = () => {
    onClose();
    triggerRef?.current?.focus();
  };

  if (!dialog) return null;
  const { mode, packageCount } = dialog;
  const isRequest = mode === 'request';
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div role="dialog" aria-modal="true" className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[85dvh] overflow-y-auto">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isRequest ? 'bg-amber-50 dark:bg-amber-900/30' : 'bg-red-50 dark:bg-red-900/30'}`}>
          <svg className={`w-6 h-6 ${isRequest ? 'text-amber-500' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
          {isRequest ? 'Request Cancellation' : 'Confirm Cancellation'}
        </h2>
        {packageCount > 1 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            <span className="font-semibold text-red-600 dark:text-red-400">Package deal:</span> This car is part of a {packageCount}-car deal.{' '}
            {isRequest
              ? 'The seller will need to confirm cancellation of the entire deal.'
              : `Confirming will cancel all ${packageCount} cars in this deal.`}
          </p>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            {isRequest
              ? 'A cancellation request will be sent to the seller. The transaction continues until they confirm.'
              : 'This will permanently cancel the transaction. This cannot be undone.'}
          </p>
        )}
        <div className="flex gap-2">
          <button onClick={handleClose} className="btn-secondary flex-1" disabled={loading}>Back</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? 'Processing…' : isRequest ? 'Send Request' : 'Confirm Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BuyerPending() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightTxnId = searchParams.get('txn') ? Number(searchParams.get('txn')) : null;
  const requestedTab = searchParams.get('tab');
  const [tab, setTab] = useState(
    requestedTab === 'history' ? 'history' : requestedTab === 'active' ? 'active' : 'bids'
  );
  const [bidPendingCount, setBidPendingCount] = useState(0);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [schedulingInspection, setSchedulingInspection] = useState(null);
  const [closingPickup, setClosingPickup] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  const [error, setError] = useState(null);
  const cancelTriggerRef = React.useRef(null);
  const toast = useToast();

  useEffect(() => {
    let mounted = true;
    api.get('/transactions?view=buyer')
      .then(d  => { if (mounted) setActive(d); })
      .catch(e => { if (mounted) setError(e.message); })
      .finally(() => { if (mounted) setLoadingActive(false); });
    return () => { mounted = false; };
  }, []);

  const switchToHistory = () => {
    setTab('history');
    if (!historyLoaded) {
      setLoadingHistory(true);
      api.get('/transactions?view=buyer&history=true')
        .then(rows => { setHistory(rows); setHistoryLoaded(true); })
        .catch(e => toast.error(`Failed to load history: ${e.message}`))
        .finally(() => setLoadingHistory(false));
    }
  };

  // A notification can deep-link straight to the history tab (?tab=history) —
  // trigger its lazy load immediately rather than waiting for a manual click.
  useEffect(() => {
    if (tab === 'history' && !historyLoaded && !loadingHistory) switchToHistory();
  }, []); // eslint-disable-line

  // Scroll a notification's target transaction into view and highlight it once
  // its list has loaded.
  useEffect(() => {
    if (!highlightTxnId) return;
    const list = tab === 'history' ? history : active;
    if (!list.length) return;
    const el = document.querySelector(`[data-txn-id="${highlightTxnId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightTxnId, tab, active, history]);

  const markPaymentSent = async (txnId) => {
    setMarkingPaid(txnId);
    try {
      const updated = await api.put(`/transactions/${txnId}/payment-sent`);
      setActive(ts => ts.map(t => t.id === txnId ? { ...t, ...updated } : t));
      toast.success('Payment marked as sent. The seller will confirm receipt.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setMarkingPaid(null);
    }
  };

  const markInspectionScheduled = async (txnId) => {
    setSchedulingInspection(txnId);
    try {
      const updated = await api.put(`/transactions/${txnId}/inspection-scheduled`);
      setActive(ts => ts.map(t => t.id === txnId ? { ...t, ...updated } : t));
      toast.success('Inspection scheduled. The seller has been notified.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSchedulingInspection(null);
    }
  };

  const markPickupComplete = async (txnId) => {
    setClosingPickup(txnId);
    try {
      const updated = await api.put(`/transactions/${txnId}/mark-closed`);
      if (updated.step === 'closed') {
        setActive(ts => ts.filter(t => t.id !== updated.id));
        toast.success('Pickup confirmed. Transaction closed.');
      } else {
        // pay-last flow: pickup confirmed, payment is now due
        setActive(ts => ts.map(t => t.id === updated.id ? { ...t, ...updated } : t));
        toast.success('Pickup confirmed. Payment is now due.');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setClosingPickup(null);
    }
  };

  // deal_id → count of active transactions for that deal
  const dealCounts = active.reduce((acc, t) => {
    if (t.deal_id) acc[t.deal_id] = (acc[t.deal_id] || 0) + 1;
    return acc;
  }, {});

  const openCancel = (txn, mode, e) => {
    cancelTriggerRef.current = e?.currentTarget || null;
    const packageCount = txn.deal_id ? (dealCounts[txn.deal_id] || 1) : 1;
    setCancelDialog({ txn, mode, packageCount });
  };

  const withdrawCancel = async (txnId) => {
    setWithdrawing(txnId);
    try {
      const updated = await api.put(`/transactions/${txnId}/withdraw-cancel`);
      setActive(ts => ts.map(t => t.id === txnId ? { ...t, ...updated } : t));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWithdrawing(null);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelLoading(true);
    try {
      if (cancelDialog.mode === 'request') {
        const { transactions: updated } = await api.put(`/transactions/${cancelDialog.txn.id}/request-cancel`);
        const map = Object.fromEntries(updated.map(t => [t.id, t]));
        setActive(ts => ts.map(t => map[t.id] ? { ...t, ...map[t.id] } : t));
      } else {
        const { ids } = await api.put(`/transactions/${cancelDialog.txn.id}/confirm-cancel`);
        setActive(ts => ts.filter(t => !ids.includes(t.id)));
      }
      setCancelDialog(null);
    } catch (err) {
      toast.error(err.message);
      setCancelDialog(null);
    } finally {
      setCancelLoading(false);
    }
  };

  const loading = tab === 'active' ? loadingActive : loadingHistory;

  // Buyer has an action when: payment is due and unsent, their flow says they
  // advance the current step, or a seller cancel needs confirming.
  const buyerHasAction = (txn) =>
    (txn.step === 'awaiting_payment' && !txn.payment_sent_at) ||
    actorFor(txn.step_flow, txn.step) === 'buyer' ||
    txn.cancel_requested_by === 'seller';

  const sorted = [...active].sort((a, b) => (buyerHasAction(b) ? 1 : 0) - (buyerHasAction(a) ? 1 : 0));

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Purchases</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        <button
          onClick={() => setTab('bids')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'bids'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          Bids
          {bidPendingCount > 0 && (
            <span className="ml-1.5 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
              {bidPendingCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('active')}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'active'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          Active
          {active.length > 0 && (
            <span className="ml-1.5 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full">
              {active.length}
            </span>
          )}
        </button>
        <button
          onClick={switchToHistory}
          className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'history'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          }`}
        >
          History
        </button>
      </div>

      {tab === 'bids' ? (
        <BidsTab onPendingChange={setBidPendingCount} />
      ) : loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : tab === 'active' ? (
        !active.length ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">No pending purchases</h3>
            <p className="text-sm text-slate-400 dark:text-slate-500">Cars you win will appear here until the transaction closes.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sorted.map((txn) => (
              <div key={txn.id} data-txn-id={txn.id} className={`card p-5 ${
                highlightTxnId === txn.id ? 'ring-2 ring-blue-400 dark:ring-blue-500'
                  : txn.cancel_requested_by === 'seller' ? 'ring-1 ring-amber-300 dark:ring-amber-700' : ''
              }`}>
                <div className="flex items-start gap-4">
                  <div className="w-24 h-16 bg-slate-100 dark:bg-slate-700 rounded-xl shrink-0 overflow-hidden flex items-center justify-center">
                    {txn.photos_videos?.[0]
                      ? <img src={txn.photos_videos[0]} alt="" className="w-full h-full object-cover" />
                      : <svg className="w-8 h-6 text-slate-300 dark:text-slate-600" viewBox="0 0 60 40" fill="currentColor"><path d="M8 28 Q12 14 20 12 L40 12 Q48 14 52 28 L54 34 Q54 37 51 37 L9 37 Q6 37 6 34 Z" opacity="0.5"/><circle cx="17" cy="37" r="5"/><circle cx="43" cy="37" r="5"/></svg>
                    }
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-slate-800 dark:text-slate-100">{carLabel(txn)}</span>
                      <StepBadge step={txn.step} />
                      {(txn.buyer_fname || txn.buyer_lname) && (
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          by {[txn.buyer_fname, txn.buyer_lname].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {txn.vin ? `VIN: ${txn.vin}` : 'No VIN'}
                      {txn.mileage ? ` · ${Number(txn.mileage).toLocaleString()} mi` : ''}
                      {dealCounts[txn.deal_id] > 1 ? ` · ${dealCounts[txn.deal_id]}-car package` : ''}
                    </p>

                    <div className="flex items-center gap-5 mt-2 flex-wrap">
                      <div>
                        <p className="text-xs text-slate-400 dark:text-slate-500">Deal</p>
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {txn.deal_name || '—'}
                          {txn.seller_account_name && (
                            <span className="font-normal text-slate-500 dark:text-slate-400"> · {txn.seller_account_name}</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 dark:text-slate-500">Winning Bid</p>
                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {txn.bid_price ? `$${formatCurrency(txn.bid_price)}` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 dark:text-slate-500">Payment Due</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300">
                          {fmtDate(txn.payment_due_date)}
                          {txn.payment_due_date && (
                            <span className="ml-1.5 text-xs text-slate-400">(<Countdown date={txn.payment_due_date} />)</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 dark:text-slate-500">Pickup / Delivery</p>
                        <p className="text-sm capitalize text-slate-700 dark:text-slate-300">{txn.pickup_or_delivery || '—'}</p>
                        {txn.seller_address && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">{[txn.seller_address, txn.seller_zip].filter(Boolean).join(' ')}</p>
                        )}
                      </div>
                    </div>

                    {txn.step !== 'cancelled' && <StepTracker step={txn.step} flow={txn.step_flow} />}

                    {/* Buyer action buttons */}
                    {!txn.cancel_requested_by && (
                      <>
                        {txn.step === 'awaiting_payment' && (
                          <div className="mt-3">
                            {txn.payment_sent_at ? (
                              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                Payment marked as sent — awaiting seller confirmation
                              </span>
                            ) : (
                              <button
                                onClick={() => markPaymentSent(txn.id)}
                                disabled={markingPaid === txn.id}
                                className="btn-primary text-xs py-1.5 px-3"
                              >
                                {markingPaid === txn.id ? 'Marking…' : 'Mark Payment Sent'}
                              </button>
                            )}
                          </div>
                        )}
                        {nextStep(txn.step_flow, txn.step) === 'inspection_scheduled' && actorFor(txn.step_flow, txn.step) === 'buyer' && (
                          <div className="mt-3">
                            <button
                              onClick={() => markInspectionScheduled(txn.id)}
                              disabled={schedulingInspection === txn.id}
                              className="btn-primary text-xs py-1.5 px-3"
                            >
                              {schedulingInspection === txn.id ? 'Marking…' : 'Mark Inspection Scheduled'}
                            </button>
                          </div>
                        )}
                        {txn.step === 'payment_confirmed' && actorFor(txn.step_flow, txn.step) === 'seller' && (
                          <WaitingMsg>Awaiting seller to mark ready for pickup</WaitingMsg>
                        )}
                        {txn.step === 'inspection_scheduled' && (
                          <WaitingMsg>Awaiting seller to mark ready for pickup</WaitingMsg>
                        )}
                        {txn.step === 'awaiting_pickup' && (
                          <div className="mt-3">
                            <button
                              onClick={() => markPickupComplete(txn.id)}
                              disabled={closingPickup === txn.id}
                              className="btn-primary text-xs py-1.5 px-3"
                            >
                              {closingPickup === txn.id
                                ? 'Confirming…'
                                : nextStep(txn.step_flow, 'awaiting_pickup') === 'closed' ? 'Confirm Pickup Complete' : 'Confirm Pickup'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Cancel row */}
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/50 flex items-center">
                  {txn.cancel_requested_by === 'buyer' ? (
                    <div className="flex items-center gap-3">
                      <WaitingMsg>Cancellation requested — awaiting seller confirmation</WaitingMsg>
                      <button
                        onClick={() => withdrawCancel(txn.id)}
                        disabled={withdrawing === txn.id}
                        className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 border border-slate-200 dark:border-slate-600 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {withdrawing === txn.id ? 'Withdrawing…' : 'Withdraw Request'}
                      </button>
                    </div>
                  ) : txn.cancel_requested_by === 'seller' ? (
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Seller requested cancellation</span>
                      <button
                        onClick={(e) => openCancel(txn, 'confirm', e)}
                        className="text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 px-2.5 py-1 rounded-lg transition-colors"
                      >
                        Confirm Cancel
                      </button>
                    </div>
                  ) : cancellable(txn.step_flow, txn.step) ? (
                    <button
                      onClick={(e) => openCancel(txn, 'request', e)}
                      className="text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-700 border border-slate-200 dark:border-slate-600 px-2.5 py-1 rounded-lg transition-colors"
                    >
                      Request Cancellation
                    </button>
                  ) : null}
                  {txn.title_delivered_at && (
                    <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Title received · {new Date(txn.title_delivered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        !history.length ? (
          <div className="card py-16 text-center">
            <p className="text-slate-400 text-sm">No completed purchases yet.</p>
            <p className="text-slate-400 text-xs mt-1">Closed transactions will appear here.</p>
          </div>
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Car</th>
                  <th className="hidden md:table-cell py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Deal</th>
                  <th className="hidden sm:table-cell py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Seller</th>
                  <th className="hidden md:table-cell py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Buyer</th>
                  <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Price Paid</th>
                  <th className="hidden sm:table-cell py-2.5 px-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Closed</th>
                  <th className="py-2.5 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {history.map(t => (
                  <tr
                    key={t.id}
                    data-txn-id={t.id}
                    onClick={() => t.deal_uuid && navigate(`/buyer/deals/${t.deal_uuid}`)}
                    onKeyDown={(e) => {
                      if (!t.deal_uuid) return;
                      if (e.key === 'Enter' || e.key === ' ' || e.code === 'Space') {
                        e.preventDefault();
                        navigate(`/buyer/deals/${t.deal_uuid}`);
                      }
                    }}
                    tabIndex={t.deal_uuid ? 0 : undefined}
                    role={t.deal_uuid ? 'button' : undefined}
                    className={`border-t border-slate-100 dark:border-slate-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${
                      t.deal_uuid ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40' : ''
                    } ${t.step === 'cancelled' ? 'opacity-60' : ''} ${
                      highlightTxnId === t.id ? 'ring-2 ring-blue-400 dark:ring-blue-500' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{carLabel(t)}</p>
                      {t.vin && [t.year, t.make].some(Boolean) && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-mono">{t.vin}</p>
                      )}
                    </td>
                    <td className="hidden md:table-cell py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                      {t.deal_name || <span className="text-slate-400 italic">Unnamed deal</span>}
                    </td>
                    <td className="hidden sm:table-cell py-3 px-4 text-sm text-slate-700 dark:text-slate-200">
                      {t.seller_account_name || '—'}
                    </td>
                    <td className="hidden md:table-cell py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                      {[t.buyer_fname, t.buyer_lname].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {t.step === 'cancelled'
                        ? <span className="text-xs font-semibold text-red-500 dark:text-red-400">Cancelled</span>
                        : <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">{money(t.bid_price)}</span>
                      }
                    </td>
                    <td className="hidden sm:table-cell py-3 px-4 text-right text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {fmtDate(t.closed_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {t.step === 'closed' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); downloadFile(`/transactions/${t.id}/document`, `deal-record-${t.id}.pdf`); }}
                          className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                        >
                          Download Record
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <CancelDialog
        dialog={cancelDialog}
        onClose={() => setCancelDialog(null)}
        onConfirm={handleCancelConfirm}
        loading={cancelLoading}
        triggerRef={cancelTriggerRef}
      />
    </div>
  );
}
